import { RSAA } from 'redux-api-middleware'
import * as R from 'ramda'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'

import { initialState, types } from 'store/Applicant'
import { cleanTempApplicants, siftApplicants } from 'utils/applicants'

/**
 * Fetches the list of applicant packages. By converting our temporary uuid into
 * a `tempUuid` and sending it, the backend will pass it back associated with
 * that applicants packages. We use it to map packages to applicants.
 *
 * Added a third argument to the fetch call, called allApplicants This is for
 * the use case where we went to manually pick the applicants. instead of
 * picking the ones in the Redux store Use case is the add member flow for ACA
 *
 * @param {string} funnelType The type of funnel or flow we are going through
 *   (e.g. signup, addmember etc...)
 * @param {string} subproduct The type of subproduct we are trying to fetch
 *   pacakages for (e.g. offExchange, onExchange etc.)
 * @param {array} manualApplicantSelection
 */
export const fetchPackages = ({
  funnelType,
  subproduct,
  manualApplicantSelection = [],
}) => {
  if (R.either(R.isNil, R.isEmpty)(funnelType)) {
    throw new Error('Must provide a funnel type to fetch packages')
  }
  return {
    [RSAA]: {
      endpoint: getApiPath(
        `acct`,
        `pub/application/packages${makeQueryParams({
          urlSlug: webStorage.getItem('ptnr'),
          funnelType,
          subproduct,
        })}`,
      ),
      headers: {
        'x-api-key': process.env.REACT_APP_X_API_KEY,
        token: 'IGNORE',
      },
      method: 'POST',
      body: ({ Applicant }) => {
        // Get the applicants from redux state.
        // Using the live applicants so that all information is already current.
        // Having applicants cleaned so that temp uuid's don't become a problem.
        const applicantsList = R.either(
          R.isEmpty,
          R.isNil,
        )(manualApplicantSelection)
          ? Object.values(Applicant.applicants.entities)
          : manualApplicantSelection

        let applicants = cleanTempApplicants(applicantsList, {
          raiseStartDate: true,
        })

        // Filter out applicants who are not covered.
        // I the primary is not being covered, don't send them as an applicant.
        // Since the primary has to exist anyway, their status will be NOT_COVERED.
        applicants = applicants.filter(
          a => a.enrollmentStatus !== 'NOT_COVERED',
        )

        return JSON.stringify({ applicants })
      },
      types: [
        types.FETCH_PACKAGES_REQUEST,
        types.FETCH_PACKAGES_SUCCESS,
        types.FETCH_PACKAGES_ERROR,
      ],
    },
  }
}

export const fetchFuturePackages = ({
  funnelType,
  subproduct,
  manualApplicantSelection = [],
  upcomingPolicyStartDate = '',
}) => {
  if (R.either(R.isNil, R.isEmpty)(funnelType)) {
    throw new Error('Must provide a funnel type to fetch packages')
  }
  return {
    [RSAA]: {
      endpoint: getApiPath(
        `acct`,
        `pub/application/packages${makeQueryParams({
          urlSlug: webStorage.getItem('ptnr'),
          funnelType,
          subproduct,
        })}`,
      ),
      headers: {
        'x-api-key': process.env.REACT_APP_X_API_KEY,
        token: 'IGNORE',
      },
      method: 'POST',
      body: ({ Applicant }) => {
        // Get the applicants from redux state.
        // Using the live applicants so that all information is already current.
        // Having applicants cleaned so that temp uuid's don't become a problem.
        const applicantsList = R.either(
          R.isEmpty,
          R.isNil,
        )(manualApplicantSelection)
          ? Object.values(Applicant.applicants.entities)
          : manualApplicantSelection

        let applicants = cleanTempApplicants(applicantsList, {
          raiseStartDate: true,
        })

        // Filter out applicants who are not covered
        // If the primary is not being covered, don't send them as an applicant.
        // Since the primary has to exist anyway, their status will be NOT_COVERED.
        applicants = applicants.filter(
          a => a.enrollmentStatus !== 'NOT_COVERED',
        )

        // Set the start date for the primary applicant to the upcoming policy start date or the next year
        // This is the main difference with the regular fetchPackage call
        // It alters the start year of ONLY the primary to the next year
        // By doing this, the end point will return a future quote
        if (upcomingPolicyStartDate) {
          applicants[0].startDate = upcomingPolicyStartDate
        } else {
          const currentYear = new Date().getFullYear()
          const futureYear = currentYear + 1
          applicants[0].startDate = new Date(
            `01-01-${futureYear}`,
          ).toISOString()
        }

        return JSON.stringify({ applicants })
      },
      types: [
        types.FETCH_FUTURE_PACKAGES_REQUEST,
        types.FETCH_FUTURE_PACKAGES_SUCCESS,
        types.FETCH_FUTURE_PACKAGES_ERROR,
      ],
    },
  }
}

/**
 * Selects a specified package for a specified applicant.
 *
 * @param {str} uuid The uuid of the applicant to select the package for
 * @param {str} packageType The type of package to select
 */
export const selectPackage = (uuid, packageName) => ({
  type: types.SELECT_PACKAGE,
  payload: { uuid, packageName },
})

/** Clears all the packages in the store */
export const clearPackages = () => ({
  type: types.CLEAR_PACKAGES,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_FUTURE_PACKAGES_REQUEST: {
      return {
        ...state,
        futurePackages: {
          ...state.futurePackages,
          busy: 'all',
          error: '',
        },
      }
    }

    // TODO E2E test this. Not convinced if refactored properly with zero regression.
    case types.FETCH_FUTURE_PACKAGES_SUCCESS: {
      const packagesByType = R.pipe(
        R.converge(R.zipObj, [
          R.map(R.either(R.prop('uuid'), R.prop('tempUuid'))),
          R.pluck('packages'),
        ]),
        R.toPairs,
        R.map(([applicant, packages]) =>
          R.map(R.mergeRight({ applicant }))(packages),
        ),
        R.unnest,
        R.groupBy(R.prop('packageName')),
      )(payload)

      let currentSelectedPackage =
        R.path(
          ['applicants', 'primaryApplicant', 'quote', 'packageName'],
          state,
        ) || ''

      // Ensure the current selected package exists in packagesByType
      const newQuote = packagesByType[currentSelectedPackage] || []

      // Update the state with the new quote if it exists
      // The new quote object will have the most updated pricing
      // that will be used as part as the /update endpoint
      if (newQuote.length > 0) {
        return {
          ...state,
          applicants: {
            ...state.applicants,
            primaryApplicant: {
              ...state.applicants?.primaryApplicant,
              quote: newQuote[0],
            },
          },
          futurePackages: {
            ...state.futurePackages,
            busy: null,
            entities: packagesByType,
          },
        }
      }

      return {
        ...state,
        futurePackages: {
          ...state.futurePackages,
          busy: null,
          entities: packagesByType,
        },
      }
    }

    case types.FETCH_FUTURE_PACKAGES_ERROR: {
      return {
        ...state,
        futurePackages: {
          ...state.futurePackages,
          busy: null,
          error: payload.response.message,
        },
      }
    }

    case types.FETCH_PACKAGES_REQUEST: {
      return {
        ...state,
        packages: {
          ...state.packages,
          busy: 'all',
          error: '',
        },
      }
    }

    case types.FETCH_PACKAGES_SUCCESS: {
      const packagesByType = R.pipe(
        R.converge(R.zipObj, [
          R.map(R.either(R.prop('uuid'), R.prop('tempUuid'))),
          R.pluck('packages'),
        ]),
        R.toPairs,
        R.map(([applicant, packages]) =>
          R.map(R.mergeRight({ applicant }))(packages),
        ),
        R.unnest,
        R.groupBy(R.prop('packageName')),
      )(payload)

      return {
        ...state,
        packages: {
          ...state.packages,
          busy: null,
          entities: packagesByType,
        },
      }
    }

    case types.FETCH_PACKAGES_ERROR: {
      return {
        ...state,
        packages: {
          ...state.packages,
          busy: null,
          error: payload.response.message,
        },
      }
    }

    // ----------

    case types.SELECT_PACKAGE: {
      const { uuid, packageName } = payload
      const packages = R.path(['packages', 'entities', packageName], state)

      const selectedPackage = R.find(p => p.applicant === uuid, packages)

      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          entities: {
            ...state.applicants.entities,
            [uuid]: {
              ...state.applicants.entities[uuid],
              quote: selectedPackage,
            },
          },
        },
      }

      return siftApplicants(nextState)
    }

    // ----------

    case types.CLEAR_PACKAGES: {
      return {
        ...state,
        packages: initialState.packages,
      }
    }

    // ----------

    default:
      return state
  }
}
